// One file over one data channel (variant B of spike S3).
//
// Sender: binary frames with a SHA-256 per 64 KiB chunk; never more than WINDOW bytes
// unacknowledged nor HIGH_WATER queued in the channel. Receiver: strictly in-order processing,
// acks every ACK_EVERY bytes, writes through a sink that may push back. If the channel dies the
// sender just stops: resuming is the receiver's call (a new 'get' from its contiguous offset).

import { decodeFrame, encodeFrame } from '../core/frames';
import { ACK_EVERY, HIGH_WATER, LOW_WATER, WINDOW, CHUNK } from './protocol';
import type { Sink } from './sinks';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export type ReadFn = (offset: number, len: number) => Promise<Uint8Array<ArrayBuffer>>;

export function fileReader(file: Blob): ReadFn {
  return async (offset, len) => new Uint8Array(await file.slice(offset, offset + len).arrayBuffer());
}

/** Resolves when the receiver confirmed the whole file; rejects if the channel dies or on abort. */
export async function sendFile(
  dc: RTCDataChannel,
  read: ReadFn,
  size: number,
  start: number,
  { signal, onAck }: { signal?: AbortSignal; onAck?: (acked: number) => void } = {},
): Promise<void> {
  dc.binaryType = 'arraybuffer';
  dc.bufferedAmountLowThreshold = LOW_WATER;
  let offset = start;
  let acked = start;
  let seq = 0;
  let wake: (() => void) | null = null;
  let done = false;
  const nudge = () => {
    const w = wake;
    wake = null;
    w?.();
  };
  dc.onmessage = (e) => {
    if (typeof e.data !== 'string') return;
    let m: { t?: string; offset?: number };
    try {
      m = JSON.parse(e.data);
    } catch {
      return;
    }
    if (m.t === 'ack' && typeof m.offset === 'number') {
      acked = Math.max(acked, Math.min(m.offset, size));
      onAck?.(acked);
    }
    if (m.t === 'done') done = true;
    nudge();
  };
  dc.onbufferedamountlow = nudge;
  dc.onclose = nudge;
  signal?.addEventListener('abort', nudge);

  // pc.close() does not always fire 'close' on its channels, so waits also poll readyState.
  const dead = () => dc.readyState !== 'open' || !!signal?.aborted;
  const pause = () => Promise.race([new Promise<void>((r) => (wake = r)), sleep(250)]);

  while (offset < size) {
    while (!dead() && (offset - acked > WINDOW || dc.bufferedAmount > HIGH_WATER)) await pause();
    if (dead()) throw new Error(signal?.aborted ? 'aborted' : 'channel closed');
    const len = Math.min(CHUNK, size - offset);
    dc.send(await encodeFrame(seq++, offset, await read(offset, len)));
    offset += len;
  }
  if (dead()) throw new Error('channel closed');
  dc.send(JSON.stringify({ t: 'end', size }));
  while (!done && !dead()) await pause();
  if (!done) throw new Error('channel closed before done');
}

export type ReceiveState = { contiguous: number; hashFailures: number };

/**
 * Feeds one channel's frames into a sink, starting at `state.contiguous` (shared across the
 * successive channels of a resumed file). Resolves when the file is complete.
 */
export function receiveInto(
  dc: RTCDataChannel,
  sink: Sink,
  size: number,
  state: ReceiveState,
  onProgress: (contiguous: number) => void,
): { done: Promise<void>; drain: () => Promise<void> } {
  dc.binaryType = 'arraybuffer';
  let queue: Promise<void> = Promise.resolve();
  let lastAck = state.contiguous;
  let finished = false;
  let resolveDone!: () => void;
  let rejectDone!: (e: Error) => void;
  const done = new Promise<void>((res, rej) => {
    resolveDone = res;
    rejectDone = rej;
  });

  const ack = (force = false) => {
    if ((force || state.contiguous - lastAck >= ACK_EVERY) && dc.readyState === 'open') {
      lastAck = state.contiguous;
      dc.send(JSON.stringify({ t: 'ack', offset: state.contiguous }));
    }
  };
  // Channels from ondatachannel may still be 'connecting': confirm once open. Idempotent, so
  // it is also repeated whenever the sender says 'end' (its first 'done' may have been lost).
  const sendDone = () => {
    const send = () => {
      dc.send(JSON.stringify({ t: 'ack', offset: state.contiguous }));
      dc.send(JSON.stringify({ t: 'done' }));
    };
    if (dc.readyState === 'open') send();
    else if (dc.readyState === 'connecting') dc.addEventListener('open', send, { once: true });
  };
  const finish = () => {
    if (!finished) {
      finished = true;
      resolveDone();
    }
    sendDone();
  };

  async function onFrame(buf: ArrayBuffer) {
    if (finished) return;
    const f = await decodeFrame(buf);
    if (f.offset < state.contiguous) return; // duplicate after a resume
    if (f.offset > state.contiguous) throw new Error('gap in stream');
    if (f.hashOk === false) {
      state.hashFailures++;
      throw new Error('integrity check failed');
    }
    await sink.write(f.offset, f.payload);
    state.contiguous += f.payload.byteLength;
    onProgress(state.contiguous);
    ack();
    if (state.contiguous === size) finish();
  }

  dc.onmessage = (e) => {
    if (typeof e.data === 'string') {
      queue = queue.then(() => {
        if (state.contiguous === size) finish();
      });
      return;
    }
    const buf = e.data as ArrayBuffer;
    queue = queue.then(() => onFrame(buf)).catch((err: Error) => {
      rejectDone(err);
      dc.close();
    });
  };
  const onClose = () => queue.then(() => !finished && rejectDone(new Error('channel closed')));
  dc.onclose = onClose;
  // A resumed file can already be complete (the 'done' was lost with the old channel).
  if (state.contiguous === size) queue = queue.then(finish);
  return { done, drain: () => queue };
}
